import { useEffect, useRef } from 'react';
import { type FieldErrors } from 'react-hook-form';

import { analytics } from '@services/AnalyticsService';

export const NEXT_BUTTON_TEST_ID = 'sign-up-next-button';

// After index.html's 2 s splash, which covers the page, and the sheet's slide.
const LAYOUT_MEASURE_DELAY_MS = 3000;
const MAX_JS_ERROR_REPORTS = 5;

const describeElement = (element: Element | null) =>
  element?.getAttribute('data-test-id') ?? element?.tagName ?? '';

/** Is «Далее» on screen, and is it what a tap in its middle would hit? */
export const measureNextButton = () => {
  const viewport = {
    inner_h: window.innerHeight,
    viewport_h: Math.round(window.visualViewport?.height ?? 0),
  };
  const button = document.querySelector(
    `[data-test-id="${NEXT_BUTTON_TEST_ID}-container"]`,
  );

  if (!button) return { next_found: false, ...viewport };

  const { top, bottom, left, width, height } = button.getBoundingClientRect();
  const tapTarget = document.elementFromPoint(
    left + width / 2,
    top + height / 2,
  );

  return {
    next_found: true,
    next_top: Math.round(top),
    next_bottom: Math.round(bottom),
    next_tappable: tapTarget !== null && button.contains(tapTarget),
    tap_target: describeElement(tapTarget),
    ...viewport,
  };
};

/**
 * Fields the browser itself holds back: `requestSubmit` stops at a `required`
 * one with its own bubble, so the form's validation never runs.
 */
export const listNativeInvalidFields = (form: HTMLFormElement | null) =>
  Array.from(form?.querySelectorAll(':invalid') ?? [])
    .map(describeElement)
    .join(',');

/** Field names and the schema's own texts: nothing the user typed. */
export const describeInvalidFields = (invalidFields: FieldErrors) => {
  const fields = Object.entries(invalidFields);

  return {
    fields: fields.map(([name]) => name).join(','),
    types: fields.map(([, error]) => String(error?.type)).join(','),
    messages: fields.map(([, error]) => String(error?.message)).join(' | '),
  };
};

interface SignUpStepLogParams {
  step: string;
  index: number;
  total: number;
  isRoleStepShown: boolean;
  isLoading: boolean;
}

/** Logs each step as it appears, then how «Далее» sits once it has settled. */
export const useSignUpStepLog = ({
  step,
  index,
  total,
  isRoleStepShown,
  isLoading,
}: SignUpStepLogParams) => {
  useEffect(() => {
    if (isLoading) return;

    analytics.trackSignUpStep('shown', {
      step,
      index,
      total,
      role_step: isRoleStepShown,
    });

    const timer = setTimeout(() => {
      // A diagnostic must never break the sign-up it watches.
      try {
        analytics.trackSignUpStep('layout', { step, ...measureNextButton() });
      } catch {
        void 0;
      }
    }, LAYOUT_MEASURE_DELAY_MS);

    return () => clearTimeout(timer);
  }, [step, index, total, isRoleStepShown, isLoading]);
};

/** Errors that no boundary sees, such as a throw in a tap handler. */
export const useSignUpErrorLog = () => {
  const reportCount = useRef(0);

  useEffect(() => {
    const report = (message: string) => {
      if (reportCount.current >= MAX_JS_ERROR_REPORTS) return;

      reportCount.current += 1;
      analytics.trackSignUpStep('js_error', { message });
    };
    const handleError = (event: ErrorEvent) => report(event.message);
    const handleRejection = (event: PromiseRejectionEvent) =>
      report(String(event.reason));

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, []);
};
